import { createPortal } from "react-dom";
import { formatDate } from "@/lib/plant";
import { inPot } from "@/lib/gardenChecks";

const TYPE_LABEL = { raised: "Raised Bed", polytunnel: "Polytunnel", open: "Open Ground", container: "Container", tray: "Seed Tray" };

function PrintBed({ bed, vegBySlug }) {
  const map = {};
  (bed.placements || []).forEach((p) => { map[`${p.col}-${p.row}`] = p; });
  const size = Math.min(34, Math.floor(640 / bed.cols));
  const cells = [];
  for (let r = 0; r < bed.rows; r++) for (let c = 0; c < bed.cols; c++) {
    const v = map[`${c}-${r}`] && vegBySlug[map[`${c}-${r}`].slug];
    const off = !inPot(bed, c, r);
    cells.push(
      <div key={`${c}-${r}`} style={{ width: size, height: size, border: off ? "none" : "1px solid #cbd5c0", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {v && <img src={v.icon} alt={v.name} style={{ width: "85%", height: "85%", objectFit: "contain" }} />}
      </div>
    );
  }
  return (
    <div style={{ breakInside: "avoid", marginBottom: 18 }}>
      <h3 style={{ fontWeight: 700, fontSize: 14, margin: "0 0 6px" }}>{bed.name} <span style={{ fontWeight: 400, color: "#666" }}>· {TYPE_LABEL[bed.type] || bed.type} · {bed.cols}×{bed.rows}</span></h3>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${bed.cols}, ${size}px)`, border: "2px solid #6b7f5e", borderRadius: bed.type === "container" ? "50%" : 6, width: "max-content", overflow: "hidden", padding: bed.type === "container" ? 4 : 0 }}>
        {cells}
      </div>
    </div>
  );
}

export function PlanPrint({ plan, vegBySlug, schedule }) {
  if (!plan) return null;
  const counts = {};
  plan.beds.forEach((b) => (b.placements || []).forEach((p) => { counts[p.slug] = (counts[p.slug] || 0) + 1; }));
  const sched = {};
  (schedule?.items || []).forEach((it) => { sched[it.slug] = it; });
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const td = { padding: "6px 8px", borderBottom: "1px solid #ddd", fontSize: 12, textAlign: "left" };

  return createPortal(
    <div id="print-root" data-testid="plan-print" style={{ fontFamily: "Georgia, serif", color: "#1a2e1a", padding: 12 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{plan.name}</h1>
      <p style={{ fontSize: 12, color: "#555", margin: "4px 0 18px" }}>
        Sprout Log garden layout · {plan.unit === "imperial" ? `${plan.cell_size}in` : `${plan.cell_size}cm`} squares · printed {formatDate(new Date().toISOString())}
      </p>
      {plan.beds.map((b) => <PrintBed key={b.id} bed={b} vegBySlug={vegBySlug} />)}
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "18px 0 6px", breakBefore: "auto" }}>Plant list &amp; dates</h2>
      {rows.length === 0 ? <p style={{ fontSize: 12 }}>No crops planted yet.</p> : (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>{["Crop", "Count", "Sow", "Transplant", "Harvest"].map((h) => <th key={h} style={{ ...td, fontWeight: 700, borderBottom: "2px solid #6b7f5e" }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(([slug, n]) => {
              const v = vegBySlug[slug]; const s = sched[slug];
              return (
                <tr key={slug}>
                  <td style={td}>{v?.name || slug}</td>
                  <td style={td}>{n}</td>
                  <td style={td}>{s?.sow_date ? formatDate(s.sow_date) : "—"}</td>
                  <td style={td}>{s?.method === "indoors" && s?.transplant_date ? formatDate(s.transplant_date) : "—"}</td>
                  <td style={td}>{s?.harvest_date ? `~${formatDate(s.harvest_date)}` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {schedule?.needs_frost_date && <p style={{ fontSize: 11, color: "#777", marginTop: 8 }}>Set your last frost date in Sprout Log to add sowing and harvest dates.</p>}
    </div>,
    document.body
  );
}
